<template>
  <div id="app">
    <Loader />
    <router-view/>
    <notification-prompt v-if="isAuthenticated" />
  </div>
</template>

<script>

import NotificationPrompt from '@/components/shared/NotificationsPrompt';
import { mapGetters } from 'vuex';
import { ACCESS_TOKEN, NOTIFICATIONS, USER } from '@/utils/const';
import firebase from 'firebase/app';
import 'firebase/analytics';
import helpers from '@/utils/helpers';
import unleashMixin from '@/mixins/unleashMixin';
import { version } from '../package.json';
import Loader from './components/loader/Loader';

const BOTPRESS_INJECT_ID = 'botpress-inject-script';
const BOTPRESS_CONFIG_ID = 'botpress-config-script';
const BOTPRESS_INJECT_SRC = process.env.VUE_APP_BOTPRESS_INJECT_SRC;
const BOTPRESS_CONFIG_SRC = process.env.VUE_APP_BOTPRESS_CONFIG_SRC;

export default {
  name: 'App',
  mixins: [unleashMixin],
  components: { NotificationPrompt },
  Loader,
  computed: {
    ...mapGetters('auth', ['isAuthenticated']),
  },
  watch: {
    unleashUpdated() {
      this.syncBotpressWidget();
    },
  },
  metaInfo: {
    title: 'APA Insurance, Welcome to Happiness',
    titleTemplate: 'Motor Customer Portal | %s',
    changed(metaInfo) {
      firebase.analytics().setCurrentScreen(metaInfo.title);
      firebase.analytics().logEvent('page_view');
      firebase.analytics().logEvent('screen_view', {
        app_name: helpers.isPWA() ? 'pwa' : 'web',
        screen_name: metaInfo.title,
        app_version: version,
      });
    },
  },
  methods: {
    syncBotpressWidget() {
      // If Unleash has never responded (unreachable/down), preserve the current widget state.
      if (this.unleashUpdated === 0) return;

      const variant = this.getFlagVariant('SET_HAPI_FREE');
      const shouldShow = variant?.featureEnabled === true || variant?.feature_enabled === true;

      if (shouldShow) {
        const appendConfigScript = () => {
          if (document.getElementById(BOTPRESS_CONFIG_ID)) {
            return;
          }
          const configScript = document.createElement('script');
          configScript.id = BOTPRESS_CONFIG_ID;
          configScript.src = BOTPRESS_CONFIG_SRC;
          document.body.appendChild(configScript);
        };

        if (document.getElementById(BOTPRESS_INJECT_ID)) {
          // inject.js already loaded (e.g. Unleash flag toggled on later)
          appendConfigScript();
        } else {
          // inject.js not yet in DOM — add it and wait for it to load
          // before appending the config script (defer is ignored on dynamic scripts)
          const injectScript = document.createElement('script');
          injectScript.id = BOTPRESS_INJECT_ID;
          injectScript.src = BOTPRESS_INJECT_SRC;
          injectScript.onload = appendConfigScript;
          document.body.appendChild(injectScript);
        }
      } else {
        // Remove both scripts if the flag is disabled
        [BOTPRESS_INJECT_ID, BOTPRESS_CONFIG_ID].forEach((id) => {
          const el = document.getElementById(id);
          if (el) el.remove();
        });
      }
    },
  },
  beforeCreate() {
    if (this.$store.getters['auth/isAuthenticated'] && (this.$cookies.get(USER) === null || this.$cookies.get(ACCESS_TOKEN) === null)) {
      this.$store.dispatch('auth/signOut').then(() => {
        this.$store.commit('setClient', []);
        localStorage.removeItem(NOTIFICATIONS);
        this.$router.push({ name: 'SignIn' });
      });
    }
  },
  created() {
    window.addEventListener('offline', () => {
      this.$toast.error('Seems you\'re offline!', 'No Internet');
    });
    window.addEventListener('online', () => {
      this.$toast.success('You\'re back online', 'Online');
      if (window.location.pathname === '/no-internet') {
        this.$router.go(-1);
      }
    });
  },
  mounted() {
    // Run once on mount — Unleash may already have cached flags from a previous session
    this.syncBotpressWidget();
  },
};
</script>
